import { useDialog } from 'components/core/Dialog/common/DialogContext';
import { getWarnableChanges } from 'shared/domain/documentation/documentationAsMapWarnings';
import { documentationClient } from 'shared/domain/documentation/documentationClient';
import { levelClient } from 'shared/domain/level/levelClient';
import { isAdmin } from 'shared/domain/role/isAdmin';
import { useCurrentUserRole } from 'store/user';
import { useCancelConfirmation } from 'presentation/dialogForms/dialogFormsHooks';
import {
  ComponentType,
  createContext,
  FC,
  ReactElement,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { showErrorToastAndLog } from 'services/toaster';
import { useLevels } from 'store/level';
import { useWarningsOnLevelChange } from './model';
import {
  DocumentationEditTarget,
  DocumentationFields,
  DocumentationFormContext,
  SubmitForm,
} from './types';
import { showWarningsAsDialog } from './warningsDialog';

const WithDocumentationFormContext = createContext<
  DocumentationFormContext | undefined
>(undefined);

const WithDocumentationForm: FC<{ children?: ReactNode }> = ({
  children,
}) => {
  const {
    items: levels,
  } = useLevels();
  const compileWarnings = useWarningsOnLevelChange(getWarnableChanges);
  const createDialog = useDialog();

  const formName: 'documentation' = 'documentation';
  const SUBMIT_EVENT_TYPE = 'documentation_submit_event';
  const [isPosting, setIsPosting] = useState(false);

  const role = useCurrentUserRole();

  const [isOpen, setIsOpen] = useState(false);
  const confirmCancel = useCancelConfirmation('documentation');
  const closeDialog = useCallback(async () => {
    const { cancelConfirmed } = await confirmCancel();
    if (!cancelConfirmed) {
      return;
    }
    setIsOpen(false);
  }, [confirmCancel]);
  const openDialog = useCallback(() => {
    setIsOpen(true);
  }, []);

  const submitForm: SubmitForm = useCallback(
    (values: DocumentationFields, { documentation, onSuccess }: DocumentationEditTarget) => {
      const { localId } = documentation;
      if (typeof localId !== 'number') {
        showErrorToastAndLog(
          'Error while updating a documentation: LocalId is not a number.'
        );
        return Promise.reject('LocalId is not a number.');
      }
      const warnings = compileWarnings(values.levels, levels, documentation);
      return showWarningsAsDialog(createDialog, warnings, documentation).then((result) => {
        if (!result.warningsConfirmed) {
          return;
        }
        setIsPosting(true);
        // An admin's edit of a documentation used as a map is confirmed by the
        // level update; wait for it from before the edit is sent.
        const levelUpdate = isAdmin(role) ? levelClient.waitForUpdate() : undefined;
        levelUpdate?.catch(() => undefined);

        return documentationClient
          .edit({ ...values, localId })
          .then(() => levelUpdate)
          .then(() => {
            setIsPosting(false);
            onSuccess();
          })
          .catch((error: unknown) => {
            showErrorToastAndLog('Error while updating a documentation: submit timed out or failed.', error);
            setIsPosting(false);
          });
      });
    },
    [role, compileWarnings, levels, createDialog]
  );
  const releaseSubmitEvent: () => void = useCallback(() => {
    window.dispatchEvent(new CustomEvent(SUBMIT_EVENT_TYPE));
  }, []);

  const ctx = useMemo(() => {
    return {
      isOpen,
      closeDialog,
      openDialog,
      submitForm,
      isPosting,
      SUBMIT_EVENT_TYPE,
      releaseSubmitEvent,
      formName,
    };
  }, [
    isOpen,
    closeDialog,
    openDialog,
    isPosting,
    releaseSubmitEvent,
    submitForm,
  ]);
  return (
    <WithDocumentationFormContext.Provider value={ctx}>
      {children}
    </WithDocumentationFormContext.Provider>
  );
};

export function useDocumentationForm(): DocumentationFormContext {
  const context = useContext(WithDocumentationFormContext);
  if (context === undefined) {
    throw new Error(
      'useDocumentationForm must be used within an DocumentationFormContext'
    );
  }
  return context;
}

export const withDocumentationForm =
  (Component: ComponentType<any>) =>
  ({ ...props }): ReactElement => (
    <WithDocumentationForm>
      <Component {...props} />
    </WithDocumentationForm>
  );
